import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { CamerallaLogin } from "src/app/shared/services/authentication-services/cameralla-login";
import { CamerallaAuthService } from "../../../../shared/services/authentication-services/cameralla-auth.service";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  submitted = false;
  returnUrl: string;
  error = "";
  showPassword = false;
  alertMessage: string = "";
  alertType: string = "";
  errorMessages = {
    TEM02: "ERROR: Incorrect username or password.",
    TEM03: "ERROR: Incorrect email or password.",
    TEM04:
      "ERROR: The Username or e-mail field and the Password field are required.",
    TEM05: "ERROR: The Username or e-mail field is required.",
    TEM06: "ERROR: The Password field is required.",
    TEM07: "ERROR: Invalid username or email.",
    TEM08: "ERROR: The network connection was lost. Please try again.",
    TEM09:
      "ERROR: There was an error processing your request. Please try again.",
    TEM10: "ERROR: Failed to save the new password. Please try again later.",
    TEM11:
      "ERROR: Password was already reset using this link. Please enter your email again.",
    TEM12:
      "ERROR: Your password reset link has expired. Please enter your email again.",
  };

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: CamerallaLogin,
    private camerallaAuthService: CamerallaAuthService,
    private toastr: ToastrService
  ) {
    if (this.camerallaAuthService.isAuthenticated()) {
      this.router.navigate(["/auth"]);
    }
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      username: ["", [Validators.required, this.usernameValidator]],
      password: ["", [Validators.required, this.passwordValidator]],
      rememberMe: [false],
    });

    this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/auth";

    const savedRedirectUrl = localStorage.getItem("miss-login");
    if (savedRedirectUrl) {
      this.returnUrl = JSON.parse(savedRedirectUrl);
    }

    this.loadRememberedCredentials();
  }

  usernameValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }

    const value = control.value.trim();

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailPattern.test(value)) {
      const domainPattern = /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(value)) {
        return { invalidFormat: true };
      }
    } else {
      if (value.length === 0) {
        return { invalidFormat: true };
      }
    }

    return null;
  }

  passwordValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }

    const password = control.value;

    const minLength = 12;
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? ]/.test(
      password
    );

    if (
      password.length < minLength ||
      !hasLowerCase ||
      !hasUpperCase ||
      !hasNumber ||
      !hasSpecialChar
    ) {
      return { invalidPassword: true };
    }

    return null;
  }

  get formControls() {
    return this.loginForm.controls;
  }

  showAlert(
    message: string,
    type: "info" | "error" | "success" | "warning" = "error"
  ): void {
    this.alertMessage = message;
    this.alertType = `alert-${type}`;

    if (type !== "error") {
      setTimeout(() => {
        this.clearAlert();
      }, 5000);
    }
  }

  clearAlert(): void {
    this.alertMessage = "";
    this.alertType = "";
  }

  loadRememberedCredentials(): void {
    const rememberedCredentials = localStorage.getItem(
      "remembered-credentials"
    );
    if (rememberedCredentials) {
      const credentials = JSON.parse(rememberedCredentials);
      this.loginForm.patchValue({
        username: credentials.username,
        rememberMe: true,
      });
    }
  }

  validateusername(input: string): { isEmail: boolean; isValid: boolean } {
    if (!input || input.trim().length === 0) {
      return { isEmail: false, isValid: false };
    }

    const trimmedInput = input.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = emailRegex.test(trimmedInput);

    if (isEmail) {
      const domainPattern = /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const isValid = domainPattern.test(trimmedInput);
      return { isEmail: true, isValid };
    } else {
      const isValid = trimmedInput.length > 0;
      return { isEmail: false, isValid };
    }
  }

  getErrorMessage(): string {
    return this.error;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onUsernameBlur(): void {
    const usernameControl = this.formControls.username;
    const value = usernameControl.value;

    if (value && value.trim()) {
      const validation = this.validateusername(value);
      if (!validation.isValid) {
        usernameControl.markAsTouched();
      }
    }
  }

  onUsernameInput(): void {
    if (this.alertMessage) {
      this.clearAlert();
    }
  }

  getUsernameInputType(): string {
    const value = this.formControls.username.value;
    if (!value) return "none";

    const validation = this.validateusername(value);
    if (validation.isEmail) {
      return validation.isValid ? "valid-email" : "invalid-email";
    } else {
      return validation.isValid ? "valid-username" : "invalid-username";
    }
  }

  onSubmit(): void {
    this.submitted = true;
    this.clearAlert();

    if (this.loginForm.invalid) {
      this.handleValidationErrors();
      return;
    }

    const { username, password, rememberMe } = this.loginForm.value;
    const usernameValue = username ? username.trim() : "";
    const validation = this.validateusername(usernameValue);

    if (!validation.isValid) {
      this.showAlert(this.errorMessages.TEM07, "error");
      return;
    }

    const loginData = {
      username: usernameValue,
      password: password,
    };

    this.authService.digitalLogin(loginData).then((response: any) => {
      if (response.Data) {
        if (rememberMe) {
          localStorage.setItem(
            "remembered-credentials",
            JSON.stringify({
              username: usernameValue,
            })
          );
        } else {
          localStorage.removeItem("remembered-credentials");
        }

        const user = { ...response.Data.user, userId: response.Data.userId };
        localStorage.setItem("dataUser", JSON.stringify(user));
        localStorage.removeItem("miss-login");
        setTimeout(() => {
          this.router.navigate(["/overview"]);
        }, 50);
      }
    });
  }

  private handleValidationErrors(): void {
    const usernameControl = this.formControls.username;
    const passwordControl = this.formControls.password;
    const usernameErrors = usernameControl.errors;
    const passwordErrors = passwordControl.errors;

    // BR02 validation rules - Show alert messages below logo
    // Check if both fields are empty (TEM04)
    if (
      usernameErrors &&
      usernameErrors["required"] &&
      passwordErrors &&
      passwordErrors["required"]
    ) {
      this.showAlert(this.errorMessages.TEM04, "error");
      return;
    }

    // Check if only username is empty (TEM05)
    if (usernameErrors && usernameErrors["required"]) {
      this.showAlert(this.errorMessages.TEM05, "error");
      return;
    }

    // Check if only password is empty (TEM06)
    if (passwordErrors && passwordErrors["required"]) {
      this.showAlert(this.errorMessages.TEM06, "error");
      return;
    }

    // Check for invalid username/email format (TEM07)
    if (usernameErrors && usernameErrors["invalidFormat"]) {
      this.showAlert(this.errorMessages.TEM07, "error");
      return;
    }

    // Check for invalid password format
    if (passwordErrors && passwordErrors["invalidPassword"]) {
      this.showAlert(
        "Password must be at least 12 characters with 1 lowercase, 1 uppercase, 1 number, and 1 special character",
        "error"
      );
      return;
    }
  }
}
